import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { petAPI } from '../../../../services/api';
import RestrictedVisitModal from '../../../reception/RestrictedVisitModal';
import SOAPDetailsContent from '../../../reception/SOAPDetailsContent';


const MedicalHistoryTab = ({ petId }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeInternalTab, setActiveInternalTab] = useState('vaccination');
    const [pastVisits, setPastVisits] = useState([]);
    const [loadingVisits, setLoadingVisits] = useState(false);
    const [selectedVisit, setSelectedVisit] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!petId) return;
        setLoading(true);
        setError(null);
        try {
            const response = await petAPI.getCompleteMedicalHistory(petId);
            if (response.data.status === 'success') {
                setHistory(response.data.data);
            } else {
                setError(t('receptionOutpatients.medicalHistory.errors.genericMessage', { defaultValue: 'Failed to load medical history' }));
            }
        } catch (err) {
            console.error('Error fetching medical history:', err);
            // Check if it's a 500 error (backend issue)
            if (err.response?.status === 500) {
                setError('Unable to load medical history due to a server error. Please try again later or contact support if the issue persists.');
            } else {
                setError('Failed to load medical history. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }, [petId, t]);

    const fetchPastVisits = useCallback(async () => {
        if (!petId) return;
        setLoadingVisits(true);
        try {
            const response = await petAPI.getPetVisits(petId);
            if (response.data.status === 'success') {
                const visits = response.data.data || [];
                // Only show visits that are checked out, completed, or closed
                const filtered = visits.filter(v =>
                    !['active', 'admitted', 'registered', 'pending', 'in_progress', 'ongoing', 'cancelled', 'canceled'].includes(String(v.status || '').toLowerCase())
                );
                setPastVisits(filtered);
            } else if (Array.isArray(response.data)) {
                const filtered = response.data.filter(v =>
                    !['active', 'admitted', 'registered', 'pending', 'in_progress', 'ongoing', 'cancelled', 'canceled'].includes(String(v.status || '').toLowerCase())
                );
                setPastVisits(filtered);
            }
        } catch (err) {
            console.error('Error fetching past visits:', err);
        } finally {
            setLoadingVisits(false);
        }
    }, [petId]);

    useEffect(() => {
        if (activeInternalTab === 'past_visits' || activeInternalTab === 'medication') {
            fetchPastVisits();
        }
    }, [activeInternalTab, fetchPastVisits]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const formatDate = (dateString) => {
        if (!dateString) return '---';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleDateString(i18n.language, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const medicationList = useMemo(() => {
        const historyMeds = history?.medication_history || [];
        const serviceMeds = (pastVisits || []).flatMap((visit) => {
            const services = visit.medication_services || [];
            return services.map((service, index) => ({
                id: `service-${visit.id}-${service.id || index}`,
                title: service.title || service.name_en || service.name_ar,
                medication_name: service.title || service.name_en || service.name_ar,
                date: visit.visit_date,
                start_date: visit.visit_date,
                veterinarian: visit.doctor_name,
                note: [
                    `${visit.type === 'inpatient'
                        ? t('receptionInpatients.title', { defaultValue: 'Inpatient' })
                        : t('receptionOutpatients.title', { defaultValue: 'Outpatient' })}${visit.serial_num ? ` · ${visit.serial_num}` : ''}`,
                    service.qty ? `${t('common.qty', { defaultValue: 'Qty' })}: ${service.qty}` : null,
                    service.notes || null,
                    service.due_date ? `${t('common.dueDate', { defaultValue: 'Due' })}: ${service.due_date}` : null,
                ].filter(Boolean).join(' · '),
            }));
        });
        return [...serviceMeds, ...historyMeds];
    }, [history, pastVisits, t]);

    if (loading) return (
        <div className="tab-loading">
            <div className="spinner"></div>
            <p>{t('common.loading')}</p>
        </div>
    );

    if (error) return (
        <div className="tab-error" style={{ 
            padding: '24px', 
            textAlign: 'center',
            color: '#ef4444'
        }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', marginBottom: '12px' }}></i>
            <p style={{ margin: '8px 0', fontWeight: 500 }}>{error}</p>
            <button 
                onClick={fetchHistory}
                style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                }}
            >
                {t('visitReports.retry', { defaultValue: 'Retry' })}
            </button>
        </div>
    );

    if (!history) return <div className="tab-empty">{t('common.noData')}</div>;

    const internalTabs = [
        { key: 'vaccination', label: t('receptionOutpatients.medicalHistory.tabs.vaccination'), icon: 'fas fa-syringe', tint: 'teal' },
        { key: 'medication', label: t('receptionOutpatients.medicalHistory.tabs.medication'), icon: 'fas fa-pills', tint: 'blue' },
        { key: 'allergies', label: t('receptionOutpatients.medicalHistory.tabs.allergies'), icon: 'fas fa-allergies', tint: 'red' },
        { key: 'illness', label: t('receptionOutpatients.medicalHistory.tabs.illness'), icon: 'fas fa-disease', tint: 'amber' },
        { key: 'surgery', label: t('receptionOutpatients.medicalHistory.tabs.surgery'), icon: 'fas fa-procedures', tint: 'purple' },
        { key: 'dental', label: t('receptionOutpatients.medicalHistory.tabs.dental'), icon: 'fas fa-tooth', tint: 'teal' },
        { key: 'deworming', label: t('receptionOutpatients.medicalHistory.tabs.deworming', { defaultValue: 'Deworming & Anti-Flea' }), icon: 'fas fa-bug', tint: 'amber' },
        { key: 'past_visits', label: t('receptionInpatients.details.tabs.pastVisits', { defaultValue: 'Past Visits' }), icon: 'fas fa-history', tint: 'gray' }
    ];

    const activeTab = internalTabs.find(tab => tab.key === activeInternalTab) || internalTabs[0];

    const getField = (obj, fields) => {
        for (const field of fields) {
            if (obj[field] !== undefined && obj[field] !== null) return obj[field];
        }
        return null;
    };

    const vetDetail = (record) => {
        const vet = getField(record, ['veterinarian', 'Veterinarian']);
        return vet || null;
    };

    const dateVetDetail = (record, dateFields, extra) => {
        const parts = [formatDate(getField(record, dateFields)), vetDetail(record), extra].filter(Boolean);
        return parts.join(' · ');
    };

    // Normalized rows for the active category — data extraction identical to the old cards
    const buildRows = () => {
        switch (activeInternalTab) {
            case 'vaccination':
                return (history.vaccination_history || []).map((v, i) => ({
                    id: v.id ?? `v-${i}`,
                    title: getField(v, ['title', 'vaccine_name', 'Vaccine Name']) || '---',
                    detail: dateVetDetail(v, ['date', 'date_administered', 'Date Administered'],
                        v.next_due_date ? `${t('receptionOutpatients.details.medicalHistory.nextDue', { defaultValue: 'Next' })}: ${formatDate(getField(v, ['next_due_date', 'Next Due Date']))}` : null),
                    note: v.note || null,
                }));
            case 'medication':
                return medicationList.map((m, i) => ({
                    id: m.id ?? `m-${i}`,
                    title: getField(m, ['title', 'medication_name', 'Medication Name']) || '---',
                    detail: dateVetDetail(m, ['date', 'start_date', 'Start Date']),
                    note: m.note || null,
                }));
            case 'allergies':
                return (history.current_allergies || []).map((a, i) => ({
                    id: a.id ?? `a-${i}`,
                    title: getField(a, ['title', 'allergen', 'Allergen']) || (typeof a === 'string' ? a : '---'),
                    detail: formatDate(getField(a, ['date', 'date_recorded', 'Date Recorded'])),
                    note: a.reaction ? `${t('receptionOutpatients.details.medicalHistory.reaction', { defaultValue: 'Reaction' })}: ${a.reaction}` : (a.note || null),
                    severity: a.severity || null,
                }));
            case 'illness': {
                const records = [...(history.past_illness_history || []), ...(history.chronic_conditions || [])];
                return records.map((ill, i) => ({
                    id: ill.id ?? `ill-${i}`,
                    title: getField(ill, ['title', 'illness_name', 'condition_name', 'Illness Name', 'Condition Name']) || '---',
                    detail: dateVetDetail(ill, ['date', 'diagnosis_date', 'Diagnosis Date']),
                    note: ill.treatment_plan ? `${t('receptionOutpatients.details.medicalHistory.treatment', { defaultValue: 'Treatment' })}: ${ill.treatment_plan}` : (ill.note || null),
                }));
            }
            case 'surgery':
                return (history.surgical_history || []).map((s, i) => ({
                    id: s.id ?? `s-${i}`,
                    title: getField(s, ['title', 'surgery_name', 'Surgery Name']) || '---',
                    detail: dateVetDetail(s, ['date', 'Date']),
                    note: s.note || null,
                }));
            case 'dental':
                return (history.dental_records || []).map((d, i) => ({
                    id: d.id ?? `d-${i}`,
                    title: getField(d, ['title', 'dental_procedure', 'Dental Procedure']) || '---',
                    detail: dateVetDetail(d, ['date', 'Date']),
                    note: d.note || null,
                }));
            case 'deworming': {
                const records = [...(history.deworming_anti_flea_history || []), ...(history.deworming_anti_flea || [])];
                return records.map((dew, i) => ({
                    id: dew.id ?? `dew-${i}`,
                    title: getField(dew, ['title', 'treatment_name', 'treatment', 'type', 'Type']) || '---',
                    detail: dateVetDetail(dew, ['date', 'date_administered', 'Date'],
                        dew.next_due_date ? `${t('receptionOutpatients.details.medicalHistory.nextDue', { defaultValue: 'Next' })}: ${formatDate(getField(dew, ['next_due_date', 'Next Due Date']))}` : null),
                    note: dew.note || null,
                }));
            }
            case 'past_visits':
                return pastVisits.map((visit, i) => ({
                    id: visit.id ?? `visit-${i}`,
                    title: `${visit.type === 'inpatient'
                        ? t('receptionInpatients.title', { defaultValue: 'Inpatient Visit' })
                        : t('receptionOutpatients.title', { defaultValue: 'Outpatient Visit' })}${visit.serial_num ? ` · #${visit.serial_num}` : ''}`,
                    detail: [
                        formatDate(visit.visit_date || visit.created_at),
                        visit.status,
                        visit.doctor_name || null,
                    ].filter(Boolean).join(' · '),
                    note: visit.reason || null,
                    onClick: () => setSelectedVisit(visit),
                    trailing: (
                        <span className="mh-row-trailing">
                            <i className="fas fa-eye"></i>{' '}
                            {visit.can_view_full_soap === false
                                ? t('common.viewFinalDiagnosis', { defaultValue: 'View Final Diagnosis' })
                                : t('common.viewDetails', { defaultValue: 'View SOAP' })}
                        </span>
                    ),
                }));
            default:
                return [];
        }
    };

    const rows = buildRows();

    const emptyText = activeInternalTab === 'past_visits'
        ? t('common.noData')
        : t(`receptionOutpatients.medicalHistory.sections.${activeInternalTab}.empty`);

    return (
        <div className={`emr-tab-content tab-content-inner ${isRTL ? 'rtl' : ''}`}>
            <div className="emr-horizontal-tabs">
                {internalTabs.map(tab => (
                    <button
                        key={tab.key}
                        className={`horizontal-tab-item ${activeInternalTab === tab.key ? 'active' : ''}`}
                        onClick={() => setActiveInternalTab(tab.key)}
                    >
                        <i className={tab.icon}></i>
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>
            <div className="emr-content-area mh-area">
                {/* One-line section header: name · count + refresh */}
                <div className="mh-section-head">
                    <h4 className="mh-section-title">{activeTab.label}</h4>
                    <span className="mh-count-chip">{rows.length}</span>
                    <button
                        className="mh-refresh"
                        onClick={fetchHistory}
                        title={t('receptionOutpatients.details.medicalHistory.refresh', { defaultValue: 'Refresh' })}
                    >
                        <i className="fas fa-sync-alt"></i>
                    </button>
                </div>

                <div className="mh-body">
                    {activeInternalTab === 'past_visits' && loadingVisits ? (
                        <div className="tab-loading"><div className="spinner"></div><p>{t('common.loading')}</p></div>
                    ) : rows.length > 0 ? (
                        <div className="mh-list">
                            {rows.map((row) => (
                                <div
                                    key={row.id}
                                    className="mh-row"
                                    style={row.onClick ? { cursor: 'pointer' } : undefined}
                                    onClick={row.onClick}
                                >
                                    <span className={`mh-row-ic ${activeTab.tint}`}>
                                        <i className={activeTab.icon}></i>
                                    </span>
                                    <span className="mh-row-who">
                                        <span className="mh-row-name">{row.title}</span>
                                        {row.detail && row.detail !== '---' && (
                                            <span className="mh-row-detail">{row.detail}</span>
                                        )}
                                        {row.note && <span className="mh-row-note">{row.note}</span>}
                                    </span>
                                    {row.severity && (
                                        <span className={`severity-badge ${String(row.severity).toLowerCase()}`}>{row.severity}</span>
                                    )}
                                    {row.trailing}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="mh-empty">
                            <i className={`${activeTab.icon} mh-empty-icon`}></i>
                            <p className="mh-empty-text">{emptyText}</p>
                            <p className="mh-empty-hint">
                                {t('receptionOutpatients.details.medicalHistory.emptyHint', { defaultValue: 'Records added during visits will appear here' })}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {selectedVisit && selectedVisit.can_view_full_soap !== false && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    zIndex: 10000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }} onClick={() => setSelectedVisit(null)}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        width: '90%',
                        maxWidth: '900px',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{
                            padding: '16px',
                            borderBottom: '1px solid #eee',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{ margin: 0 }}>{t('receptionInpatients.details.tabs.soapNotes', { defaultValue: 'SOAP Notes' })} - {formatDate(selectedVisit.visit_date || selectedVisit.created_at)}</h3>
                            <button onClick={() => setSelectedVisit(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            <SOAPDetailsContent
                                isVisible={true}
                                inpatientId={String(selectedVisit.type || '').toLowerCase() === 'inpatient' ? (selectedVisit.id || selectedVisit.visit_id || selectedVisit.inpatient_visit_id || selectedVisit.inpatient_id || selectedVisit.pet_visit_id) : null}
                                outpatientId={(String(selectedVisit.type || '').toLowerCase() === 'outpatient' || !selectedVisit.type) ? (selectedVisit.id || selectedVisit.visit_id || selectedVisit.outpatient_visit_id || selectedVisit.outpatient_id || selectedVisit.pet_visit_id) : null}
                                readOnly={true}
                                hideHeader={true}
                                petId={petId}
                                services={selectedVisit.services || selectedVisit.visit_details || selectedVisit.visitDetails || []}
                                visitInfo={{
                                    visitDate: selectedVisit.visit_date || selectedVisit.created_at,
                                    initialDiagnosis: selectedVisit.reason || '',
                                    finalDiagnosis: selectedVisit.final_diag || selectedVisit.final_diagnosis || '',
                                    doctorNotes: selectedVisit.doctor_notes || '',
                                    prescription: selectedVisit.prescription || ''
                                }}
                                onClose={() => setSelectedVisit(null)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {selectedVisit && selectedVisit.can_view_full_soap === false && (
                <RestrictedVisitModal visit={selectedVisit} onClose={() => setSelectedVisit(null)} />
            )}
        </div>
    );
};

export default MedicalHistoryTab;
